import React, { useState, FormEvent, ChangeEvent, useCallback } from 'react';
import { AnalysisResult, UploadError } from '../types';
import { ExclamationTriangleIcon, ShieldCheckIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { validateImage, compressImage } from '../utils/imageUtils';
import LoadingSpinner from './LoadingSpinner';

// Add these constants at the top of the file, outside the component
const PHISHING_INDICATORS = {
  urgency: [
    'urgent', 'immediate', 'action required', 'account suspended', 'security alert',
    'unauthorized access', 'suspicious activity', 'limited time', 'expires soon',
    'account blocked', 'verify now', 'important notice', 'final warning',
    'account will be suspended', 'unauthorized login detected', 'immediate action required',
    'act now or lose access', 'within 24 hours', 'deactivated', 'suspended',
    'last chance', 'time sensitive', 'expires today'
  ],
  financial_scam: [
    'you\'ve won', 'claim your prize', 'claim your refund', 'lottery winner',
    'inheritance', 'unclaimed funds', 'payment pending', 'wire transfer',
    'bank transfer', 'western union', 'money gram', 'cash prize',
    'million dollars', 'payment release', 'tax refund'
  ],
  security_warnings: [
    'unusual activity', 'update your password', 'verify your identity',
    'suspicious login', 'security breach', 'account compromised',
    'unauthorized access', 'login attempt', 'security update required',
    'privacy at risk', 'hacker is using your account', 'account access limited'
  ],
  generic_greetings: [
    'dear user', 'dear customer', 'dear account holder', 'dear member',
    'valued customer', 'dear sir/madam', 'dear email user', 'dear client',
    'dear beneficiary', 'hello dear', 'attention user'
  ],
  sensitive_requests: [
    'confirm password', 'verify password', 'enter credentials',
    'update payment', 'confirm payment', 'verify payment details',
    'confirm identity', 'social security', 'ssn', 'credit card',
    'card details', 'banking details', 'account number',
    'verification code', 'one-time password', 'otp', '2fa code'
  ],
  emotional_manipulation: [
    'your privacy is at risk', 'security compromised', 'account at risk',
    'unauthorized charges', 'suspicious purchases', 'unusual login attempts',
    'protect your account', 'secure your account', 'prevent unauthorized access'
  ],
  common_targets: [
    'paypal', 'apple', 'microsoft', 'amazon', 'google', 'facebook',
    'netflix', 'bank', 'wells fargo', 'chase', 'citi', 'dhl',
    'fedex', 'ups', 'usps', 'irs', 'linkedin', 'instagram'
  ],
  suspicious_domains: [
    'secure-', 'account-', 'banking-', 'login-', 'verify-',
    '-secure', '-verify', '-account', '-login', '-support',
    'security', 'support', 'service', 'update', 'confirm'
  ],
  suspicious_urls: [
    'bit.ly', 'tinyurl.com', 'goo.gl', 'shorturl.at', 'ow.ly',
    'pay-pal', 'paypa1', 'apple-id', 'microsoft-verify',
    'amazon-account', 'google-docs', 'facebook-login'
  ],
  malicious_attachments: [
    '.exe', '.zip', '.rar', '.7z', '.js', '.vbs', '.bat',
    '.scr', '.cmd', '.msi', '.jar', '.docm', '.xlsm', '.pptm'
  ]
};

const RISK_WEIGHTS = {
  urgency: 20,
  financial_scam: 25,
  security_warnings: 20,
  generic_greetings: 10,
  sensitive_requests: 30,
  emotional_manipulation: 20,
  common_targets: 15,
  suspicious_domains: 25,
  suspicious_urls: 30,
  spoofed_sender: 35,
  malicious_attachments: 40,
  poor_formatting: 15,
  ip_mismatch: 25
};

// Add this new function to check for email spoofing patterns
const checkEmailSpoofing = (email: string, displayName: string = ''): boolean => {
  const commonTargets = PHISHING_INDICATORS.common_targets;
  const domain = email.toLowerCase().split('@')[1];
  
  // Check for slight misspellings of common domains
  const isSpoofedDomain = commonTargets.some(target => {
    const targetDomain = `${target}.com`;
    return domain !== targetDomain && 
           (domain?.includes(target) || 
            (domain && levenshteinDistance(domain, targetDomain) <= 2));
  });

  // Check for mismatched display name
  const hasTargetInDisplayName = Boolean(
    displayName && 
    commonTargets.some(target => 
      displayName.toLowerCase().includes(target) && 
      !email.toLowerCase().includes(target))
  );

  return Boolean(isSpoofedDomain || hasTargetInDisplayName);
};

// Add this helper function for string similarity
const levenshteinDistance = (str1: string, str2: string): number => {
  const track = Array(str2.length + 1).fill(null).map(() =>
    Array(str1.length + 1).fill(null));
  for (let i = 0; i <= str1.length; i += 1) {
    track[0][i] = i;
  }
  for (let j = 0; j <= str2.length; j += 1) {
    track[j][0] = j;
  }
  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1,
        track[j - 1][i] + 1,
        track[j - 1][i - 1] + indicator
      );
    }
  }
  return track[str2.length][str1.length];
};

export default function EmailAnalyzer() {
  const [emailContent, setEmailContent] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const handleImageUpload = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset states
    setUploadError(null);
    setIsUploading(true);

    try {
      // Validate file
      const error = validateImage(file);
      if (error) {
        setUploadError(error);
        return;
      }

      // Compress image
      const compressedImage = await compressImage(file);
      const compressedFile = new File([compressedImage], file.name, {
        type: 'image/jpeg',
      });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(compressedFile);

      setImageFile(compressedFile);
    } catch (error) {
      setUploadError({
        message: 'Failed to process image',
        code: 'UPLOAD_FAILED',
      });
    } finally {
      setIsUploading(false);
    }
  }, []);

  const analyzeText = (text: string): {
    indicators: string[];
    categories: Set<string>;
    riskScore: number;
    detailedAnalysis: {
      spoofedSender: boolean;
      suspiciousUrls: string[];
      maliciousAttachments: string[];
      sensitiveRequests: string[];
      formatIssues: string[];
    };
  } => {
    const indicators: string[] = [];
    const categories = new Set<string>();
    let riskScore = 0;

    const normalizedText = text.toLowerCase();

    // Check all phishing indicators
    Object.entries(PHISHING_INDICATORS).forEach(([category, keywords]) => {
      keywords.forEach(keyword => {
        if (normalizedText.includes(keyword.toLowerCase())) {
          indicators.push(keyword);
          categories.add(category);
          riskScore += RISK_WEIGHTS[category as keyof typeof RISK_WEIGHTS];
        }
      });
    });

    // Check for suspicious URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = normalizedText.match(urlRegex) || [];
    if (urls.length > 0) {
      const suspiciousUrlPatterns = [
        'bit.ly', 'goo.gl', 'tinyurl.com', 'shorturl.at',
        'secure', 'account', 'login', 'verify', 'update'
      ];
      
      urls.forEach(url => {
        if (suspiciousUrlPatterns.some(pattern => url.includes(pattern))) {
          indicators.push(`Suspicious URL: ${url}`);
          riskScore += RISK_WEIGHTS.suspicious_urls;
        }
      });
    }

    // Check for email spoofing patterns
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const emails = normalizedText.match(emailRegex) || [];
    if (emails.length > 0) {
      emails.forEach(email => {
        const [, domain] = email.split('@');
        if (PHISHING_INDICATORS.common_targets.some(target => 
          domain.includes(target.toLowerCase()) && !email.endsWith(`@${target.toLowerCase()}.com`)
        )) {
          indicators.push(`Possible spoofed sender: ${email}`);
          riskScore += RISK_WEIGHTS.spoofed_sender;
        }
      });
    }

    // Add checks for all new indicators
    const detailedAnalysis = {
      spoofedSender: false,
      suspiciousUrls: [] as string[],
      maliciousAttachments: [] as string[],
      sensitiveRequests: [] as string[],
      formatIssues: [] as string[]
    };

    // Check for email spoofing
    const emailMatches = Array.from(text.matchAll(/([^<]*)<([^>]+)>|([^<]+)/g));
    for (const match of emailMatches) {
      const displayName = match[1]?.trim() || '';
      const email = (match[2] || match[3])?.trim();
      if (email && checkEmailSpoofing(email, displayName)) {
        detailedAnalysis.spoofedSender = true;
        indicators.push(`Suspicious sender: ${displayName} <${email}>`);
        riskScore += RISK_WEIGHTS.spoofed_sender;
      }
    }

    // Check for malicious attachments
    const attachmentRegex = /\.[a-zA-Z0-9]{2,4}\b/g;
    const attachments = text.match(attachmentRegex) || [];
    attachments.forEach(ext => {
      if (PHISHING_INDICATORS.malicious_attachments.includes(ext.toLowerCase())) {
        detailedAnalysis.maliciousAttachments.push(ext);
        indicators.push(`Suspicious attachment type: ${ext}`);
        riskScore += RISK_WEIGHTS.malicious_attachments;
      }
    });

    // Check for poor formatting
    const formatIssues = [];
    if (text.split('\n').some(line => line.length > 100)) {
      formatIssues.push('Unusually long lines');
    }
    if (text.match(/[A-Z]{5,}/g)) {
      formatIssues.push('Excessive capitalization');
    }
    if (formatIssues.length > 0) {
      detailedAnalysis.formatIssues = formatIssues;
      riskScore += RISK_WEIGHTS.poor_formatting;
    }

    // Normalize risk score to 0-100 range
    riskScore = Math.min(Math.max(Math.round(riskScore), 30), 100);

    return { indicators, categories, riskScore, detailedAnalysis };
  };

  const handleAnalyze = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsAnalyzing(true);
    setResult(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      let extractedText = emailContent;
      
      if (imageFile) {
        // Simulate OCR processing
        await new Promise(resolve => setTimeout(resolve, 1000));
        extractedText += `\nSimulated OCR text from image...`;
      }

      const analysis = analyzeText(extractedText);

      const mockResult: AnalysisResult = {
        riskScore: analysis.riskScore,
        headerAnalysis: {
          spoofedSender: analysis.detailedAnalysis.spoofedSender,
          spfPass: analysis.riskScore < 50,
          dmarcPass: analysis.riskScore < 50,
        },
        domainAnalysis: {
          domainAge: analysis.riskScore > 70 ? 5 : 365,
          suspiciousDomain: analysis.riskScore > 60,
          blacklisted: analysis.riskScore > 80,
        },
        linkAnalysis: {
          suspiciousLinks: analysis.indicators
            .filter(i => i.startsWith('Suspicious URL:'))
            .map(i => i.replace('Suspicious URL: ', '')),
          totalLinks: (extractedText.match(/(https?:\/\/[^\s]+)/g) || []).length,
        },
        attachmentAnalysis: {
          suspiciousFiles: analysis.detailedAnalysis.maliciousAttachments,
          totalAttachments: analysis.detailedAnalysis.maliciousAttachments.length,
        },
        keywordAnalysis: {
          suspiciousKeywords: analysis.indicators.filter(i => !i.startsWith('Suspicious URL:')),
          urgencyScore: analysis.categories.has('urgency') ? 0.8 : 
                       analysis.categories.has('pressure') ? 0.6 : 0.2,
        },
        imageAnalysis: imageFile ? {
          extractedText: "Simulated OCR text from image...",
          confidence: 0.85,
          detectedLogos: Array.from(analysis.categories.has('common_targets') ? 
            PHISHING_INDICATORS.common_targets.filter(target => 
              extractedText.toLowerCase().includes(target.toLowerCase())
            ) : []),
        } : undefined,
      };

      setResult(mockResult);
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">
        Phishing Email Analyzer
      </h1>

      <form onSubmit={handleAnalyze} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Email Screenshot
            </label>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex justify-center items-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-md appearance-none hover:border-primary-500 focus:outline-none"
                >
                  <div className="flex flex-col items-center space-y-2">
                    {isUploading ? (
                      <>
                        <LoadingSpinner size="md" />
                        <span className="text-sm text-gray-500">Processing...</span>
                      </>
                    ) : (
                      <>
                        <PhotoIcon className="h-8 w-8 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          Click to upload screenshot
                        </span>
                      </>
                    )}
                  </div>
                  <input
                    id="image-upload"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploading}
                  />
                </label>
              </div>
              {imagePreview && (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Email preview"
                    className="h-32 w-auto rounded-md"
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    className="absolute -top-2 -right-2 rounded-full bg-red-500 text-white p-1 text-xs"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-2 bg-gray-50 text-sm text-gray-500">OR</span>
            </div>
          </div>

          <div>
            <label
              htmlFor="email-content"
              className="block text-sm font-medium text-gray-700"
            >
              Paste Email Content
            </label>
            <textarea
              id="email-content"
              rows={10}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={emailContent}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setEmailContent(e.target.value)}
              placeholder="Paste the email content here..."
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isAnalyzing || isUploading || (!emailContent && !imageFile)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {isAnalyzing ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" />
              <span>Analyzing...</span>
            </div>
          ) : (
            'Analyze Email'
          )}
        </button>
      </form>

      {uploadError && (
        <div className="mt-2 text-sm text-red-600">
          {uploadError.message}
        </div>
      )}

      {result && (
        <div className="mt-8 space-y-6">
          <div className="rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                {result.riskScore > 70 ? (
                  <ExclamationTriangleIcon className="h-8 w-8 text-danger-500" />
                ) : (
                  <ShieldCheckIcon className="h-8 w-8 text-success-500" />
                )}
                <div className="ml-4">
                  <h2 className="text-xl font-semibold">
                    Risk Score: {result.riskScore}%
                  </h2>
                  <p className="text-gray-600">
                    {result.riskScore > 70
                      ? 'This email shows strong signs of being a phishing attempt.'
                      : 'This email appears to be legitimate.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Analysis Results */}
          <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium">Keyword Analysis</h3>
              <div className="mt-3">
                <p>Suspicious Keywords Found: {result.keywordAnalysis.suspiciousKeywords.join(', ') || 'None'}</p>
                <p>Urgency Score: {(result.keywordAnalysis.urgencyScore * 100).toFixed(0)}%</p>
              </div>
            </div>

            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium">Link Analysis</h3>
              <div className="mt-3">
                <p>Total Links: {result.linkAnalysis.totalLinks}</p>
                <p>Suspicious Links: {result.linkAnalysis.suspiciousLinks.length}</p>
              </div>
            </div>

            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium">Domain Analysis</h3>
              <div className="mt-3">
                <p>Domain Age: {result.domainAnalysis.domainAge} days</p>
                <p>Blacklisted: {result.domainAnalysis.blacklisted ? 'Yes' : 'No'}</p>
              </div>
            </div>
          </div>

          {/* Add image analysis results */}
          {result?.imageAnalysis && (
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium">Image Analysis</h3>
              <div className="mt-3">
                <p>OCR Confidence: {(result.imageAnalysis.confidence * 100).toFixed(0)}%</p>
                {result.imageAnalysis.detectedLogos && (
                  <p>Detected Logos: {result.imageAnalysis.detectedLogos.join(', ')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
} 